import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import { useMaterialUIController } from "context";
import { useAuth } from "auth/AuthContext";
import ProtectedRoute from "auth/ProtectedRoute";
import brand from "assets/images/jcl-logo.png";
import LanguageSwitcher from "components/JCL/LanguageSwitcher";

const MAX_CACHED_WORKSPACES = 4;

function PersistentWorkspace({ workspaceRoutes, activeRouteKey }) {
  const [cachedKeys, setCachedKeys] = useState(() =>
    activeRouteKey ? [activeRouteKey] : []
  );

  useEffect(() => {
    if (!activeRouteKey) return;

    setCachedKeys((current) => {
      const next = [
        activeRouteKey,
        ...current.filter((key) => key !== activeRouteKey),
      ].slice(0, MAX_CACHED_WORKSPACES);

      const unchanged =
        next.length === current.length &&
        next.every((key, index) => key === current[index]);

      return unchanged ? current : next;
    });
  }, [activeRouteKey]);

  const mountedKeys = activeRouteKey
    ? [activeRouteKey, ...cachedKeys.filter((key) => key !== activeRouteKey)]
    : cachedKeys;

  return workspaceRoutes
    .filter((route) => mountedKeys.includes(route.key))
    .map((route) => {
      const isActive = route.key === activeRouteKey;

      return (
        <div
          key={route.key}
          data-jcl-workspace={route.key}
          aria-hidden={!isActive}
          style={{ display: isActive ? "block" : "none" }}
        >
          <ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>
        </div>
      );
    });
}

PersistentWorkspace.propTypes = {
  workspaceRoutes: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeRouteKey: PropTypes.string,
};

PersistentWorkspace.defaultProps = {
  activeRouteKey: null,
};

export default function App() {
  const [controller] = useMaterialUIController();
  const { layout, sidenavColor, darkMode } = controller;
  const { pathname } = useLocation();
  const { isAuthenticated, profile } = useAuth();

  const visibleRoutes = useMemo(
    () => routes.filter((route) => !route.roles || route.roles.includes(profile?.role)),
    [profile]
  );

  const workspaceRoutes = useMemo(
    () => visibleRoutes.filter((route) => route.route && !route.public),
    [visibleRoutes]
  );

  const activeWorkspaceRoute = useMemo(
    () => workspaceRoutes.find((route) => route.route === pathname) || null,
    [pathname, workspaceRoutes]
  );

  useEffect(() => {
    document.body.setAttribute("dir", "ltr");
  }, []);

  useLayoutEffect(() => {
    const storageKey = `jcl-scroll:${pathname}`;
    const saved = Number(sessionStorage.getItem(storageKey) || 0);
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: Number.isFinite(saved) ? saved : 0, left: 0, behavior: "auto" });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      sessionStorage.setItem(storageKey, String(window.scrollY || 0));
    };
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (!route.route) return null;
      const element = route.public ? (
        route.component
      ) : (
        <ProtectedRoute roles={route.roles}>{route.component}</ProtectedRoute>
      );
      return <Route path={route.route} element={element} key={route.key} />;
    });

  const showPersistentWorkspace = Boolean(isAuthenticated && activeWorkspaceRoute);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <LanguageSwitcher />
      {isAuthenticated && layout === "dashboard" ? (
        <Sidenav
          color={sidenavColor}
          brand={brand}
          brandName="JCL Intelligence"
          routes={visibleRoutes}
        />
      ) : null}

      {showPersistentWorkspace ? (
        <PersistentWorkspace
          workspaceRoutes={workspaceRoutes}
          activeRouteKey={activeWorkspaceRoute.key}
        />
      ) : (
        <Routes>
          {getRoutes(routes)}
          <Route
            path="/"
            element={
              <Navigate
                to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"}
                replace
              />
            }
          />
          <Route
            path="*"
            element={
              <Navigate
                to={isAuthenticated ? "/dashboard" : "/authentication/sign-in"}
                replace
              />
            }
          />
        </Routes>
      )}
    </ThemeProvider>
  );
}
